import { useCallback, useRef, useState } from "react";
import { useThrottledCallback } from "use-debounce";

import { EltSyncUpdatedEvent } from "@/socket/ServerToClientEvents";
import { useSocketEvent } from "@/socket/SocketContext";

/**
 * Listens for elt-sync:updated socket events and triggers a throttled callback if the test function returns true.
 * @param testFn A function to test if the event should trigger the callback
 * @param callback The callback function to call
 * @param throttleMs Throttle duration in ms (default: 45000)
 */
export function useEltSyncUpdatedListener(
  testFn: (event: EltSyncUpdatedEvent) => boolean,
  callback: (event: EltSyncUpdatedEvent) => void | Promise<void>,
  throttleMs = 45_000,
) {
  // Store the latest callback in a ref to keep it stable
  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  const stableCallback = useCallback((event: EltSyncUpdatedEvent) => {
    void Promise.resolve(callbackRef.current(event)).catch((error) => {
      // eslint-disable-next-line no-console
      console.error("useEltSyncUpdatedListener callback rejected:", error);
    });
  }, []);

  // Add random jitter of ±20% to the throttle delay, stable for the hook lifetime
  const [jitteredThrottleMs] = useState(() =>
    Math.round(throttleMs * (0.8 + Math.random() * 0.4)),
  );

  const throttledCallback = useThrottledCallback(
    stableCallback,
    jitteredThrottleMs,
    {
      leading: true,
      trailing: true,
    },
  );

  useSocketEvent("elt-sync:updated", {
    onMessage(resp) {
      if (testFn(resp)) {
        throttledCallback(resp);
      }
    },
  });
}
